import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    TextField,
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    Chip,
    Alert,
    InputAdornment,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Collapse,
    Divider,
    Stack
} from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    FilterList as FilterIcon,
    GetApp as ExportIcon,
    Assessment as ReportIcon,
    Person as PersonIcon,
    Receipt as ReceiptIcon,
    DateRange as DateRangeIcon,
    TrendingUp as TrendingUpIcon,
    PeopleAlt as PeopleIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const GasTransactionReport = () => {
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({
        customerId: '',
        customerName: '',
        serialNumber: '',
        refillBoolingNumber: '',
        cashMemoNumber: '',
        startDate: '',
        endDate: '',
        sortBy: 'deliverydate',
        sortOrder: 'desc'
    });

    const { user } = useAuth();

    useEffect(() => {
        fetchTransactionReport();
    }, []);

    useEffect(() => {
        if (currentPage > 1 || pageSize !== 10) {
            fetchTransactionReport();
        }
    }, [currentPage, pageSize]);

    const fetchTransactionReport = async (searchFilters = filters) => {
        try {
            setLoading(true);
            setError('');

            // Prepare API parameters
            const params = {
                pageNumber: currentPage,
                pageSize: pageSize,
                tenantId: user?.tenantId,
                sortBy: searchFilters.sortBy,
                sortOrder: searchFilters.sortOrder
            };

            // Add filters if they have values
            if (searchFilters.customerId) params.customerId = searchFilters.customerId;
            if (searchFilters.customerName) params.customerName = searchFilters.customerName;
            if (searchFilters.serialNumber) params.serialNumber = searchFilters.serialNumber;
            if (searchFilters.refillBoolingNumber) params.refillBoolingNumber = searchFilters.refillBoolingNumber;
            if (searchFilters.cashMemoNumber) params.cashMemoNumber = searchFilters.cashMemoNumber;
            if (searchFilters.startDate) params.startDate = searchFilters.startDate;
            if (searchFilters.endDate) params.endDate = searchFilters.endDate;

            console.log('🔍 Fetching transaction report with params:', params);

            const response = await api.get('/api/gastransactions/report', { params });
            
            if (response.data) {
                setTransactions(response.data.transactions || []);
                setSummary(response.data.summary);
                setTotalRecords(response.data.totalRecords || 0);
                setTotalPages(response.data.totalPages || 0);
            }
        } catch (error) {
            console.error('Error fetching transaction report:', error);
            setError('Failed to fetch transaction report: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field) => (event) => {
        setFilters(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleApplyFilters = () => {
        setCurrentPage(1); // Reset to first page when applying filters
        fetchTransactionReport(filters);
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            customerId: '',
            customerName: '',
            serialNumber: '',
            refillBoolingNumber: '',
            cashMemoNumber: '',
            startDate: '',
            endDate: '',
            sortBy: 'deliverydate',
            sortOrder: 'desc'
        };
        setFilters(clearedFilters);
        setCurrentPage(1);
        fetchTransactionReport(clearedFilters);
    };

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    const hasActiveFilters = () => {
        return Object.entries(filters).some(([key, value]) => {
            if (key === 'sortBy' || key === 'sortOrder') return false;
            return value && value.toString().trim() !== '';
        });
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                {/* Header */}
                <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ReportIcon sx={{ mr: 2, fontSize: '2rem', color: 'primary.main' }} />
                            <Box>
                                <Typography variant="h4" gutterBottom>
                                    Gas Transaction Report
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Comprehensive analysis of gas transactions
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterIcon />}
                                onClick={() => setShowFilters(!showFilters)}
                                color={showFilters ? "primary" : "inherit"}
                            >
                                Filters {hasActiveFilters() && `(${Object.values(filters).filter(v => v && v.toString().trim()).length - 2})`}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ExportIcon />}
                                disabled={transactions.length === 0}
                            >
                                Export
                            </Button>
                        </Box>
                    </Box>

                    {/* Error Alert */}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {/* Summary Cards */}
                    {summary && (
                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <ReceiptIcon sx={{ mr: 2, color: 'primary.main' }} />
                                            <Box>
                                                <Typography color="text.secondary" gutterBottom variant="body2">
                                                    Total Transactions
                                                </Typography>
                                                <Typography variant="h6">
                                                    {summary.totalTransactions?.toLocaleString() || 0}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PeopleIcon sx={{ mr: 2, color: 'success.main' }} />
                                            <Box>
                                                <Typography color="text.secondary" gutterBottom variant="body2">
                                                    Unique Customers
                                                </Typography>
                                                <Typography variant="h6">
                                                    {summary.uniqueCustomers?.toLocaleString() || 0}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <DateRangeIcon sx={{ mr: 2, color: 'info.main' }} />
                                            <Box>
                                                <Typography color="text.secondary" gutterBottom variant="body2">
                                                    Date Range
                                                </Typography>
                                                <Typography variant="body2">
                                                    {summary.earliestTransaction ? formatDate(summary.earliestTransaction) : 'N/A'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    to {summary.latestTransaction ? formatDate(summary.latestTransaction) : 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <TrendingUpIcon sx={{ mr: 2, color: 'warning.main' }} />
                                            <Box>
                                                <Typography color="text.secondary" gutterBottom variant="body2">
                                                    Top Customer
                                                </Typography>
                                                <Typography variant="body2" noWrap>
                                                    {summary.topCustomers?.[0]?.customerName || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {summary.topCustomers?.[0]?.transactionCount || 0} transactions
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Paper>

                {/* Filters Section */}
                <Collapse in={showFilters}>
                    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Filter Options
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Customer Name"
                                    value={filters.customerName}
                                    onChange={handleFilterChange('customerName')}
                                    placeholder="Search by customer name"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Serial Number"
                                    value={filters.serialNumber}
                                    onChange={handleFilterChange('serialNumber')}
                                    placeholder="Search by serial number"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Refill Booking Number"
                                    value={filters.refillBoolingNumber}
                                    onChange={handleFilterChange('refillBoolingNumber')}
                                    placeholder="Search by refill booking number"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Cash Memo Number"
                                    value={filters.cashMemoNumber}
                                    onChange={handleFilterChange('cashMemoNumber')}
                                    placeholder="Search by cash memo number"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={filters.startDate}
                                    onChange={handleFilterChange('startDate')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={filters.endDate}
                                    onChange={handleFilterChange('endDate')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Sort By</InputLabel>
                                    <Select
                                        value={filters.sortBy}
                                        label="Sort By"
                                        onChange={handleFilterChange('sortBy')}
                                    >
                                        <MenuItem value="deliverydate">Delivery Date</MenuItem>
                                        <MenuItem value="createddate">Created Date</MenuItem>
                                        <MenuItem value="customerid">Customer ID</MenuItem>
                                        <MenuItem value="serialnumber">Serial Number</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Sort Order</InputLabel>
                                    <Select
                                        value={filters.sortOrder}
                                        label="Sort Order"
                                        onChange={handleFilterChange('sortOrder')}
                                    >
                                        <MenuItem value="asc">Ascending</MenuItem>
                                        <MenuItem value="desc">Descending</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                            >
                                Apply Filters
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                disabled={loading}
                                startIcon={<ClearIcon />}
                            >
                                Clear All
                            </Button>
                            {hasActiveFilters() && (
                                <Chip
                                    label={`${Object.values(filters).filter(v => v && v.toString().trim()).length - 2} active filters`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                        </Box>
                    </Paper>
                </Collapse>

                {/* Transactions Table */}
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h6">
                            Transaction Details
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Showing {transactions.length} of {totalRecords.toLocaleString()} transactions
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Per page</InputLabel>
                                <Select
                                    value={pageSize}
                                    label="Per page"
                                    onChange={handlePageSizeChange}
                                >
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Serial Number</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Refill Booking</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cash Memo</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Delivery Date</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.map((transaction) => (
                                            <TableRow key={transaction.id} hover>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {transaction.customerName || 'Unknown Customer'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ID: {transaction.customerId}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{transaction.serial_Number || '-'}</TableCell>
                                                <TableCell>{transaction.refill_Booling_Number || '-'}</TableCell>
                                                <TableCell>{transaction.cash_Memo_Number || '-'}</TableCell>
                                                <TableCell>{formatDate(transaction.deliveryDate)}</TableCell>
                                                <TableCell>{formatDateTime(transaction.createdDate)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {transactions.length === 0 && !loading && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography color="text.secondary" sx={{ py: 4 }}>
                                                        {hasActiveFilters() ? 
                                                            'No transactions found matching the current filters' : 
                                                            'No transactions available'
                                                        }
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Paper>

                {/* Top Customers Section */}
                {summary?.topCustomers && summary.topCustomers.length > 0 && (
                    <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Top Customers by Transaction Volume
                        </Typography>
                        <Grid container spacing={2}>
                            {summary.topCustomers.map((customer, index) => (
                                <Grid item xs={12} sm={6} md={4} key={customer.customerId}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Chip 
                                                    label={`#${index + 1}`} 
                                                    size="small" 
                                                    color={index < 3 ? "primary" : "default"}
                                                    sx={{ mr: 1 }}
                                                />
                                                <Typography variant="subtitle2" noWrap>
                                                    {customer.customerName}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {customer.transactionCount} transactions
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Last: {formatDate(customer.lastTransactionDate)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                )}
            </Box>
        </Container>
    );
};

export default GasTransactionReport;